import React, { useContext, useEffect, useRef, useState } from 'react';
import { DownloadHubModel, LinkModel, ModuleBaseModel, ModuleBasePropsModel, UserRole } from '@/types';
import { Dropdown, MetadataContext, ModalPortal, SystemFeaturesContext, UserContext } from '@/components';
import GalleryButton from '@/components/report/tools/GalleryButton';
import DownloadHubButton from '@/components/report/tools/DownloadHubButton';
import ArrowRightLight from '@/assets/arrowRightLight.svg';
import PlusLight from '@/assets/plusLight.svg';
import { useRouter } from 'next/router';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { scrollIntoView } from '@/utils';
import { useDownloadHub } from '@/components';
import classNames from 'classnames';
import StaffToolsButton from '@/components/report/tools/StaffToolsButton';
import PDFLinkButton from '@/components/report/tools/PDFLinkButton';
import Flicking from '@egjs/react-flicking';
import tailwindConfig from '@/tailwind.screens';
import { getIPLogonReportPdfLink, getReportPdfLink } from '@/api';

export type ReportNavigationBarModel = ModuleBaseModel<
  {
    sections: LinkModel[];
    childReports: LinkModel[];
    shouldDisplaySections: boolean;
    shouldDisplayAtAGlance: boolean;
    shouldDisplaySummary: boolean;
    shouldDisplayStats: boolean;
    shouldDisplayFutureInsights: boolean;
    shouldDisplayTrendEvolution: boolean;
    shouldDisplayEditorsPicks: boolean;
    shouldDisplayRelatedReports: boolean;
    downloadHub: DownloadHubModel;
    downloadLink: LinkModel;
  } & ModuleBasePropsModel
>;

const ReportNavigationBar = (data: ReportNavigationBarModel) => {
  const {
    downloadHub,
    childReports,
    shouldDisplayAtAGlance,
    shouldDisplayStats,
    shouldDisplaySections,
    shouldDisplayFutureInsights,
    shouldDisplayTrendEvolution,
    shouldDisplayEditorsPicks,
    shouldDisplayRelatedReports,
    sections,
    downloadLink,
  } = data.props;
  const [progress, setProgress] = useState(0);
  const [mobileIconsOpen, setMobileIconsOpen] = useState(false);
  const [showButtonSpeedDial, setShowButtonSpeedDial] = useState(false);
  const [pdfDownloadLink, setPdfDownloadLink] = useState<LinkModel | null>(downloadLink || null);
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations();
  const { downloadHub: downloadHubContextData, setDownloadHub, setDownloadHubPdfLink } = useDownloadHub();
  const { isPublic, pageId } = useContext(MetadataContext);
  const { stubPageLink } = useContext(SystemFeaturesContext);
  const user = useContext(UserContext);
  const iconsContainerRef = useRef<HTMLDivElement>(null);
  const iconsContainerRefWidth = useRef<number>(-1);
  const separatorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (downloadHub) {
      setDownloadHub(downloadHub);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadHub]);

  useEffect(() => {
    const onScroll = () => {
      setProgress((window.lenis?.progress || 0) * 100);
    };

    const onNativeScroll = () => {
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      setProgress((winScroll / height) * 100);
    };

    const initScroll = () => {
      setTimeout(() => {
        if (window.lenis) {
          window.lenis.on('scroll', onScroll);
        } else {
          window.addEventListener('scroll', onNativeScroll);
        }
      }, 1000);
    };

    initScroll();

    return () => {
      window.lenis?.off('scroll', onScroll);
      window.removeEventListener('scroll', onNativeScroll);
    };
  }, []);

  useEffect(() => {
    const onWindowResize = () => {
      if (iconsContainerRefWidth.current === -1 && iconsContainerRef.current?.clientWidth) {
        iconsContainerRefWidth.current = iconsContainerRef.current.clientWidth;
      }

      const separatorWidth = separatorRef.current?.clientWidth || 0;
      if (window.innerWidth < parseInt(tailwindConfig.screens.md, 10)) {
        setShowButtonSpeedDial(true);
      } else if (separatorWidth < 20) {
        setShowButtonSpeedDial(true);
      } else if (showButtonSpeedDial && separatorWidth > iconsContainerRefWidth.current + 20) {
        setShowButtonSpeedDial(false);
      }
    };
    onWindowResize();
    window.addEventListener('resize', onWindowResize);

    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, [showButtonSpeedDial]);

  useEffect(() => {
    if (downloadLink) {
      setPdfDownloadLink(downloadLink);
      setDownloadHubPdfLink(downloadLink);
    } else {
      setPdfDownloadLink(null);
      setDownloadHubPdfLink(null);
      const loc = window.location;
      const d = loc.hostname;
      const relPath = loc.href.substring(loc.origin.length);
      let pageLink = 'https://app.stylus.com' + relPath;
      if (d === 'app.stylus.com' || d === 'uat-app.testing-stylus.com') {
        pageLink = loc.href;
      }

      if (user?.isIpBasedIdentity) {
        getIPLogonReportPdfLink(pageLink, pageId).then((link) => {
          setPdfDownloadLink({ href: link });
          setDownloadHubPdfLink({ href: link });
        });
      } else if (user?.roles?.includes(UserRole.Staff)) {
        getReportPdfLink(pageLink, pageId).then((link) => {
          setPdfDownloadLink({ href: link });
          setDownloadHubPdfLink({ href: link });
        });
      } else if (user?.isAuthenticated) {
        const domain = 'https://beta.stylus.com';
        /*
        let domain = window.location.origin;
        if (domain.includes('localhost')) {
          domain = 'https://uat-app.testing-stylus.com';
        }
          */
        const endpoint = `/api/pdf-creator?url=${encodeURIComponent(domain + pathname)}&reportId=${pageId}`;

        fetch(endpoint).then((response) => {
          if (response.ok) {
            response.json().then((data) => {
              setPdfDownloadLink({ href: data.url });
              setDownloadHubPdfLink({ href: data.url });
            });
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadLink]);

  const gotoTop = () => {
    if (window.lenis) {
      window.lenis.scrollTo(0);
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const goToSection = (hash?: string) => {
    if (!hash) return;
    const element = document.getElementById(hash.replace('#', '')) as HTMLElement | null;
    if (!element) return;
    const style = window.getComputedStyle(document.body);
    if (window.lenis) {
      window.lenis.scrollTo(element, { offset: parseInt(style.getPropertyValue('--header-height'), 10) * -1 });
    } else {
      scrollIntoView(element, parseInt(style.getPropertyValue('--header-height'), 10) * -1);
    }
  };

  const iconButtons = (
    <>
      <GalleryButton tooltipPlacement={showButtonSpeedDial ? 'left' : 'top'} className="!p-3" />
      {user?.roles?.includes(UserRole.Staff) && pdfDownloadLink && (
        <PDFLinkButton
          className="!p-3"
          pdfLink={pdfDownloadLink}
          tooltipPlacement={showButtonSpeedDial ? 'left' : 'top'}
        />
      )}
      {stubPageLink && <StaffToolsButton className="!p-3" tooltipPlacement={showButtonSpeedDial ? 'left' : 'top'} />}

      <DownloadHubButton
        className="!p-3"
        data={downloadHubContextData}
        tooltipPlacement={showButtonSpeedDial ? 'left' : 'top'}
      />
    </>
  );

  return (
    <ModalPortal>
      <div className="ReportNavigationBar fixed bottom-0 left-0 flex w-full flex-col bg-white print:hidden">
        <div className="bg-gray-3 relative h-[5px] w-full">
          <div className="absolute top-0 left-0 h-full bg-black" style={{ width: progress + '%' }} />
        </div>
        <div>
          <div
            className={classNames('xxl:h-18 relative flex h-14 flex-row justify-between px-3', isPublic && 'container')}
          >
            <div>
              <Flicking
                preventClickOnDrag
                preventDefaultOnDrag
                align="prev"
                bound
                interruptable
                className="h-full [&_.flicking-camera]:items-center"
              >
                <button className="btn icon !p-3" onClick={gotoTop}>
                  <ArrowRightLight className="h-8 w-8 -rotate-90" />
                </button>

                {childReports && childReports.length > 1 && (
                  <div>
                    <Dropdown
                      name="childReports"
                      className="mx-5 max-w-[180px] shrink-0 [&_.option]:text-lg [&_.option]:whitespace-nowrap [&_.popover-title]:line-clamp-1 [&_.popover-title]:font-bold"
                      options={childReports.map((r) => ({ label: r.text || '', value: r.href }))}
                      value={pathname}
                      placement="top"
                      offset={10}
                      onChange={(e) => {
                        if (e.target.value) {
                          router.push(e.target.value);
                        }
                      }}
                      popoverMenuPortal
                      contentWrapperClassName="[&_.content]:!overflow-auto [&_.option]:text-lg [&_.option]:whitespace-nowrap"
                    />
                  </div>
                )}

                {shouldDisplayAtAGlance && (
                  <button onClick={() => goToSection('#report-at-a-glance')} className="btn mx-8 shrink-0 underline">
                    {t('report.$atAGlance')}
                  </button>
                )}

                {shouldDisplayStats && (
                  <button onClick={() => goToSection('#report-key-stats')} className="btn mx-8 shrink-0 underline">
                    {t('general.$stats')}
                  </button>
                )}

                {shouldDisplaySections && sections?.length > 1 && (
                  <div>
                    <Dropdown
                      name="sections"
                      className="mx-8 w-[170px] shrink-0 [&_.placeholder]:!text-[#a2a2a2] [&_.popover-title]:line-clamp-1 [&_.popover-title]:text-left [&_.popover-title:not(.placeholder)]:font-bold"
                      options={sections.map((section) => ({ label: section.text || '', value: section.href }))}
                      value={router.asPath}
                      placeholder={t('report.$jumpToSection')}
                      placement="top"
                      onChange={(e) => {
                        goToSection(e.target.value || undefined);
                      }}
                      popoverMenuPortal
                      contentWrapperClassName="[&_.option]:text-lg [&_.option]:whitespace-nowrap [&_.content]:!overflow-auto"
                    />
                  </div>
                )}

                {shouldDisplayFutureInsights && (
                  <button
                    onClick={() => goToSection('#report-future-insights')}
                    className="btn mx-8 shrink-0 underline"
                  >
                    {t('report.$futureInsights')}
                  </button>
                )}

                {shouldDisplayTrendEvolution && (
                  <button
                    onClick={() => goToSection('#report-trend-evolution')}
                    className="btn mx-8 shrink-0 underline"
                  >
                    {t('trendEvolution.$heading')}
                  </button>
                )}

                {shouldDisplayEditorsPicks && (
                  <button onClick={() => goToSection('#report-editors-pick')} className="btn mx-8 shrink-0 underline">
                    {t('editorsPick.$editorsPickHeading')}
                  </button>
                )}

                {shouldDisplayRelatedReports && (
                  <button onClick={() => goToSection('#report-related-items')} className="btn mx-8 shrink-0 underline">
                    {t('report.$relatedReports')}
                  </button>
                )}
              </Flicking>
            </div>

            <div ref={separatorRef} className="flex-1" />
            {!showButtonSpeedDial && (
              <div ref={iconsContainerRef} className="z-10 flex w-fit shrink-0 items-center bg-white pr-10">
                <div className="mx-10 h-1/2 w-px bg-black/10" />
                <div className="flex items-center gap-3">{iconButtons}</div>
              </div>
            )}
            {showButtonSpeedDial && (
              <div className="absolute right-8 bottom-8 z-10 flex w-fit shrink-0 items-center">
                <div className="relative">
                  <div
                    className={classNames(
                      'absolute -top-2 left-1/2 h-fit w-full -translate-x-1/2 -translate-y-full rounded-full bg-white py-4 shadow-lg',
                      mobileIconsOpen ? 'block' : 'hidden',
                    )}
                    onClick={() => setMobileIconsOpen(false)}
                  >
                    <div className="flex flex-col items-center gap-2">{iconButtons}</div>
                  </div>

                  <button
                    className="btn icon border-gray-3 h-14 w-14 rounded-full border"
                    onClick={() => setMobileIconsOpen(!mobileIconsOpen)}
                  >
                    <PlusLight className="h-7 w-7" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ReportNavigationBar;
